// The app's global keyboard bindings — ONE document listener, and the sheet
// that documents them lives here too.
//
// `SHORTCUTS` sits beside the handler on purpose. A shortcut sheet maintained
// apart from the code that binds the keys is how a help screen ends up naming a
// key nothing listens for; here the two go stale together or not at all, and
// `hotkeys.test.ts` pins that every documented combo has a handler.

import { onCleanup, onMount } from 'solid-js';

/**
 * Is this key being typed INTO something? An unmodified letter binding must
 * never fire from a text field — `?` is a character someone is entitled to type
 * into a search box, a policy editor or a reviewer note.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/** Does this event ask for the command palette? Meta OR Ctrl, so both platforms work. */
export function isPaletteChord(event: KeyboardEvent): boolean {
  if (!(event.metaKey || event.ctrlKey) || event.altKey) return false;
  return event.key.toLowerCase() === 'k';
}

/** Does this event ask for the shortcut sheet? Unmodified `?`, outside a text field. */
export function isShortcutsChord(event: KeyboardEvent): boolean {
  if (event.metaKey || event.ctrlKey || event.altKey) return false;
  return event.key === '?' && !isTypingTarget(event.target);
}

/**
 * Does this event ask to collapse or expand the rail (#7710)? Unmodified `[`,
 * outside a text field — the rule `?` follows. A layout that types `[` with
 * AltGr sends Ctrl+Alt and is refused; the rail's foot toggle is its door.
 */
export function isRailChord(event: KeyboardEvent): boolean {
  if (event.metaKey || event.ctrlKey || event.altKey) return false;
  return event.key === '[' && !isTypingTarget(event.target);
}

export type Hotkeys = {
  /** Toggle the command palette. Modified, so it fires from inside a text field too. */
  readonly onPalette: () => void;
  /** Open the shortcut sheet. */
  readonly onShortcuts: () => void;
  /**
   * Collapse or expand the rail. Answers whether it acted: the key is consumed
   * (`preventDefault`) only then, so below `lg`, where the rail is the drawer
   * and the toggle is a no-op, `[` is left alone.
   */
  readonly onRail: () => boolean;
};

/**
 * Bind the globals for the lifetime of the calling component. One listener, so
 * there is one place that decides whether a key belongs to the app or to the
 * field the user is typing in.
 */
export function createHotkeys(handlers: Hotkeys): void {
  onMount(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      // A held chord must not toggle the palette dozens of times, and a key
      // something else already handled is not ours to reinterpret.
      if (event.repeat || event.defaultPrevented) return;
      if (isPaletteChord(event)) {
        // Firefox binds Ctrl+K to the URL bar's search; without this the palette
        // opens AND focus leaves the page.
        event.preventDefault();
        handlers.onPalette();
        return;
      }
      if (isShortcutsChord(event)) {
        event.preventDefault();
        handlers.onShortcuts();
        return;
      }
      if (isRailChord(event) && handlers.onRail()) event.preventDefault();
    };
    document.addEventListener('keydown', onKeyDown);
    onCleanup(() => document.removeEventListener('keydown', onKeyDown));
  });
}

/** One row of the `?` sheet: the combos that fire it, and what it does. */
export type ShortcutDoc = {
  /** Every combo bound to it. Both are real — the handler accepts Meta and Ctrl. */
  readonly combos: readonly string[];
  /** i18n key for the description. */
  readonly label: string;
};

/** What the `?` sheet lists, in the order it lists them. */
export const SHORTCUTS: readonly ShortcutDoc[] = [
  { combos: ['⌘ K', 'Ctrl K'], label: 'shortcuts.palette' },
  { combos: ['?'], label: 'shortcuts.sheet' },
  { combos: ['['], label: 'shortcuts.rail' },
  { combos: ['↑', '↓'], label: 'shortcuts.move' },
  { combos: ['Enter'], label: 'shortcuts.open' },
  { combos: ['Esc'], label: 'shortcuts.close' },
];
